import { createSelector } from 'reselect'
import { intersection, emptyArr, isEmpty } from 'util/arrays'
import { mapObject, emptyObj } from 'util/objects'
import { parseQueryParams } from 'util/params'
import { downcase } from 'util/strings'
import { isBoostrappedSelector } from './app/base'

// Main decoration function.
export const decorate = commonWords => mailbox => {
  const label = mailbox.name
    .split(' ')
    .filter(
      word => commonWords.indexOf(word) <= -1 // keep only uncommon words
    )
    .join(' ')
    .replace(/(^at | at$|^from | from$)/g, '')
    .trim()

  // add new decorator code here

  // build one new object
  return Object.assign({}, mailbox, {
    label,
    // add new decorated props here
  })
}

// eslint-disable-next-line no-underscore-dangle
export const _selectSortedIds = state => state.mailboxes.sorted

// eslint-disable-next-line no-underscore-dangle
export const _selectById = state => state.mailboxes.byId || emptyObj

// Select only the common words across all mailboxes
// eslint-disable-next-line no-underscore-dangle
export const _selectCommonWords = createSelector(_selectById, mailboxes => {
  const names = Object.keys(mailboxes).map(id => mailboxes[id].name)
  const splitNames = names.map(name => name.split(' '))

  return intersection.apply(this, splitNames)
})

// Our main selector. This returns a denormalized collection of mailboxes
// (keyed by ID), with each mailbox object having all the
// transformation/decorations applied.
export const selectMailboxesById = createSelector(
  _selectById,
  _selectCommonWords,

  (mailboxes, commonWords) => mapObject(decorate(commonWords))(mailboxes)
)

export const selectMailbox = (state, mailboxId) => {
  return selectMailboxesById(state)[mailboxId]
}

export const selectKnownMailboxes = createSelector(
  selectMailboxesById,
  _selectSortedIds,
  (byId, sortedIds) => {
    if (sortedIds == null) {
      return emptyArr
    }
    return sortedIds.map(id => byId[id])
  }
)

export const selectMailboxesByEmail = createSelector(
  selectKnownMailboxes,
  mailboxes =>
    mailboxes.reduce((hash, mailbox) => {
      hash[mailbox.email] = mailbox // eslint-disable-line no-param-reassign
      return hash
    }, {})
)

export const selectMailboxesByName = createSelector(
  selectKnownMailboxes,
  mailboxes =>
    mailboxes.reduce((hash, mailbox) => {
      hash[mailbox.name] = mailbox // eslint-disable-line no-param-reassign
      hash[downcase(mailbox.name)] = mailbox // eslint-disable-line no-param-reassign
      return hash
    }, {})
)

export const selectMailboxesByLabel = createSelector(
  selectKnownMailboxes,
  mailboxes =>
    mailboxes.reduce((hash, mailbox) => {
      hash[mailbox.label] = mailbox // eslint-disable-line no-param-reassign
      hash[downcase(mailbox.label)] = mailbox // eslint-disable-line no-param-reassign
      return hash
    }, {})
)

export const selectMailboxes = selectKnownMailboxes

export const selectMailboxIds = createSelector(selectMailboxesById, byId =>
  Object.keys(byId)
)

export const selectFirstMailboxId = createSelector(_selectSortedIds, sorted => {
  const ids = sorted || []
  return ids[0]
})

export const selectMailboxIdFromUrl = createSelector(
  selectMailboxesByEmail,
  mailboxes => {
    const params = parseQueryParams(window.location.search)
    return params.from && mailboxes[params.from] && mailboxes[params.from].id
  }
)

export const selectAgentHasMailboxAccess = createSelector(
  isBoostrappedSelector,
  selectMailboxes,
  (isAppBootstrapped, mailboxes) => {
    // Until the app isn't bootstrapped we assume that the agent has
    // access to some mailboxes
    if (!isAppBootstrapped) return true
    return !isEmpty(mailboxes)
  }
)

export const selectMailboxesForDropdown = createSelector(
  selectKnownMailboxes,
  mailboxes =>
    mailboxes.map(({ id, name }) => {
      return {
        key: id,
        text: name,
        value: id,
      }
    })
)
